import { useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, Benutzer } from "@kundeninfo/api-client";
import { useBestaetigung } from "@kundeninfo/ui";
import { Hinweis } from "@kundeninfo/ui/me-design";
import { meldungAus } from "./gemeinsam.js";
import { CONFIRM_TEXT } from "./confirmTexts.js";
import { BILD_ACCEPT } from "../../bildAuswahl.js";
import { KiKnopf, Dateiwahl } from "../../components/meHuellen.js";
import "./Unterschrift.css";

/**
 * Die UNTERSCHRIFT eines Benutzers (Layout-Editor Etappe 1): Sie steht im
 * Unterschriftsplatz eines Beleg-Layouts, wenn dieser Benutzer Vertreter am Angebot oder
 * Auftrag ist.
 *
 * Sofort wirksam wie die Firmenlogos (begründete Ausnahme von der Modal-Hausregel): Die
 * gewählte Datei IST die Änderung; „Abbrechen" gäbe es nicht. Die Prüfung macht der
 * Server (SVG, PNG oder JPEG — Entscheid 25; 1 MB, beim SVG keine aktiven Inhalte und
 * keine Deckkraft an der Wurzel, beim Bild höchstens 2000 × 2000 Pixel); seine Meldung
 * steht hier am Block.
 *
 * Block `usig__` — eigene Klassen, nichts aus `vw__` ausgeliehen.
 */

export type UnterschriftClient =
  Pick<ApiClient, "setUserSignature" | "deleteUserSignature" | "userSignatureUrl">;

export function UnterschriftBlock(p: {
  client: UnterschriftClient; benutzer: Benutzer; onGeaendert: (neu: Benutzer) => void;
}): ReactElement {
  const [arbeitet, setArbeitet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  /** Die Erfolgsmeldung für Screenreader (K7 Fixrunde 1) — verschwindet bei der nächsten
   *  Aktion wie der Fehler-Slot, steht nie neben einem Fehler. */
  const [status, setStatus] = useState<string | null>(null);
  /** Nach dem Ersetzen dieselbe Adresse, neuer Inhalt — der Stempel lädt das Bild neu. */
  const [stempel, setStempel] = useState<number | undefined>(undefined);
  const b = p.benutzer;
  const vorhanden = b.hatUnterschrift === true;
  const { bestaetigen } = useBestaetigung();

  async function hochladen(datei: File | undefined): Promise<void> {
    // Die Dateiwahl leert sich nach jeder Wahl selbst (`nachWahlLeeren`), damit dieselbe
    // Datei nach einem Fehler erneut gewählt werden kann.
    if (datei === undefined) return;
    setFehler(null);
    setStatus(null);
    setArbeitet(true);
    try {
      const neu = await p.client.setUserSignature(b.id, datei);
      setStempel(Date.now());
      // Nachtrag E5: Am Unterschriftsplatz wirkt die Deckkraft nur bei einer SVG-Unterschrift.
      const bild = /\.(png|jpe?g)$/i.test(datei.name)
        || datei.type === "image/png" || datei.type === "image/jpeg";
      setStatus((vorhanden ? "Unterschrift ersetzt." : "Unterschrift hinterlegt.")
        + (bild ? " Deckkraft des Unterschriftsplatzes wirkt bei Bildern nicht." : ""));
      p.onGeaendert(neu);
    } catch (f) {
      setFehler(meldungAus(f, `„${datei.name}“ konnte nicht hochgeladen werden.`));
    } finally {
      setArbeitet(false);
    }
  }

  async function entfernen(): Promise<void> {
    // Ein Löschweg — erst die Rückfrage (Hausregel „Bestätigung vor Datenänderung“, 19.10.2026). Die Ausnahme oben
    // („die gewählte Datei IST die Änderung“) gilt nur dem Hochladen.
    if (!(await bestaetigen({ titel: CONFIRM_TEXT.removeTitle(CONFIRM_TEXT.signatureOf(b.name)),
      text: CONFIRM_TEXT.removeSignature, bestaetigenText: CONFIRM_TEXT.remove, gefaehrlich: true }))) return;
    setFehler(null);
    setStatus(null);
    setArbeitet(true);
    try {
      const neu = await p.client.deleteUserSignature(b.id);
      setStatus("Unterschrift entfernt.");
      p.onGeaendert(neu);
    } catch (f) {
      setFehler(meldungAus(f, "Die Unterschrift konnte nicht entfernt werden."));
    } finally {
      setArbeitet(false);
    }
  }

  return (
    <fieldset className="usig">
      <legend className="usig__titel">Unterschrift (SVG, PNG oder JPEG)</legend>
      <p className="usig__hinweis">
        Sie steht im Unterschriftsplatz eines Beleg-Layouts, wenn {b.name} Vertreter am
        Angebot oder Auftrag ist. Höchstens 1 MB, Bilder höchstens 2000 × 2000 Pixel.
      </p>
      <div className="usig__zeile">
        {vorhanden
          ? <img className="usig__bild" src={p.client.userSignatureUrl(b.id, stempel)}
              alt={`Unterschrift von ${b.name}`} />
          : <span className="usig__leer">keine Unterschrift hinterlegt</span>}
        {/* `me-eingabe art="datei"` (Entscheid 31): gesperrt während der Übertragung. */}
        <Dateiwahl className="usig__wahl" accept={BILD_ACCEPT} gesperrt={arbeitet}
          knopftext={vorhanden ? "Ersetzen" : "Hochladen"}
          name={`Unterschrift von ${b.name} hochladen`}
          nachWahlLeeren onDateien={(dateien) => { void hochladen(dateien[0]); }} />
        {vorhanden && (
          <KiKnopf deaktiviert={arbeitet} name={`Unterschrift von ${b.name} entfernen`} onClick={() => { void entfernen(); }}>
            Entfernen
          </KiKnopf>
        )}
      </div>
      {/* Seit ME-Etappe 3 (Aufgabe 8) `me-hinweis` statt eigener Zeilen; Schlechtestes oben:
          der Fehler vor Übertragung und Erfolg (beide nie zugleich mit ihm). */}
      {fehler !== null && <Hinweis art="fehler" className="usig__meldung">{fehler}</Hinweis>}
      {arbeitet && <Hinweis art="info" className="usig__meldung">Wird übertragen …</Hinweis>}
      {!arbeitet && status !== null && (
        <Hinweis art="ok" className="usig__meldung">{status}</Hinweis>
      )}
    </fieldset>
  );
}
